import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, CircularProgress, Paper, TextField } from "@mui/material";
import { get } from "lodash";
import { Controller } from "react-hook-form";
import type { FieldValues, UseControllerProps } from "react-hook-form";
import type { ListEntitiesResponse } from "client/api/ListEntitiesResponse";
import useAsyncRequest from "../../hooks/useAsyncRequest";
import useDebouncedValue from "../../hooks/useDebouncedValue";

type EntityWithId = {
  id?: string | null;
};

type EntitySelectorProps<T extends FieldValues, E extends EntityWithId> = UseControllerProps<T> & {
  label: string;
  apiRequest: (searchTerm?: string) => Promise<ListEntitiesResponse<E>>;
  optionLabelFunc: (entity: E) => string;
  required?: boolean;
  disabled?: boolean;
  variant?: "standard" | "outlined" | "filled";
  renderFooter?: () => React.ReactNode;
  renderOption?: (entity: E) => React.ReactNode;
};

const searchDebounceMs = 300;

export function EntitySelector<T extends FieldValues, E extends EntityWithId>(props: EntitySelectorProps<T, E>) {
  const {
    label,
    name,
    apiRequest,
    optionLabelFunc,
    required,
    disabled,
    variant = "outlined",
    renderFooter,
    renderOption,
  } = props;
  const [open, setOpen] = useState(false);
  const [entities, setEntities] = useState<readonly E[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const debouncedSearchTerm = useDebouncedValue(searchTerm, searchDebounceMs);
  const { data, loading, error } = useAsyncRequest(apiRequest, debouncedSearchTerm);

  useEffect(() => {
    setEntities(data?.entities || []);
  }, [data]);

  // MUI treats a slot as a distinct component type per render if the function passed to it is a
  // new reference each time — Autocomplete then unmounts and remounts the popper's listbox DOM
  // instead of just re-rendering it. That's disastrous mid-interaction: entities/loading change on
  // every keystroke and fetch, so a fresh factory here would remount the list on almost any render,
  // including the split second between a click landing and the option actually registering.
  const paperSlot = useMemo(() => {
    if (!renderFooter) {
      return undefined;
    }
    return function EntitySelectorPaper(paperProps: React.ComponentProps<typeof Paper>) {
      return (
        <Paper {...paperProps}>
          {paperProps.children}
          {renderFooter()}
        </Paper>
      );
    };
  }, [renderFooter]);

  return (
    <Controller
      {...props}
      render={({ field, formState }) => {
        const fieldErrors = get(formState.errors, name);
        const hasErrors = !!(formState.errors && fieldErrors) || !!error;

        // Include the current value in options if it's not already there
        const currentValue = field.value;
        const allOptions =
          currentValue && !entities.find((e) => e.id === currentValue?.id) ? [currentValue, ...entities] : entities;

        return (
          <Autocomplete
            open={open}
            onOpen={() => setOpen(true)}
            onClose={(_event, reason) => {
              setOpen(false);
              if (reason !== "selectOption") {
                setSearchTerm("");
              }
            }}
            disabled={disabled}
            options={allOptions}
            getOptionLabel={optionLabelFunc}
            loading={loading}
            value={field.value || null}
            onChange={(e, v) => field.onChange(v)}
            onBlur={field.onBlur}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderOption={renderOption ? (liProps, option) => <li {...liProps}>{renderOption(option)}</li> : undefined}
            renderInput={(params) => (
              <TextField
                {...params}
                ref={field.ref}
                label={label}
                error={hasErrors}
                helperText={String(fieldErrors?.message || error?.message || "")}
                variant={variant}
                required={required}
                onChange={(e) => setSearchTerm(e.target.value)}
                slotProps={{
                  ...params.slotProps,

                  input: {
                    ...params.slotProps.input,
                    endAdornment: (
                      <>
                        {loading && <CircularProgress color="inherit" size={20} />}
                        {params.slotProps.input?.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            slots={{
              paper: paperSlot,
            }}
          />
        );
      }}
    />
  );
}
